import React, { memo, useCallback } from 'react';
import { Platform, Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { SCREEN_SIZES, useScreenSize } from '../../utils/dimsHooks';
import {
  GAME_OPERATION_TYPE,
  GAME_TYPES_CONFIG,
  GAME_TYPES_TYPE,
  OPERAND_TYPE,
  SLIDER_POSITION,
} from '../../constants/constants';
import isEqual from 'lodash/isEqual';
import { renderValue } from '../screens/GameScreen/utils';
import { isMobile } from '@mightybyte/rnw.utils.device-info';
import { hasLeftOperands, hasRightOperands, hasOperands } from '../../utils/checkOperands';

interface ILargeSliderProps {
  colors: string[];
  gameType: GAME_TYPES_TYPE;
  value: OPERAND_TYPE;
  setValue: (value: OPERAND_TYPE) => void;
  position?: SLIDER_POSITION;
  operandRowButtonCount?: number;
}
const whiteLinearGradient = ['#FFF5DD', '#FFF5DD'];

const rowFullWidth = isMobile ? 162 : 250;
const rowMediumWidth = isMobile ? 120 : 250;

export const LargeSlider = memo((props: ILargeSliderProps) => {
  const { position, operandRowButtonCount } = props;
  const { width } = useWindowDimensions();

  const breakPointControls = Platform.OS === 'web' && width <= 1600;

  const rowFullWidth = isMobile || breakPointControls ? 162 : 250;
  const rowMediumWidth = isMobile || breakPointControls ? 120 : 250;

  const screenSize = useScreenSize();
  const onPress = useCallback((val: OPERAND_TYPE) => () => props.setValue(val), [props]);

  const isLarge = screenSize === SCREEN_SIZES.LARGE;
  const gradientStyle = useCallback(
    () => [styles.cell, !isLarge ? styles.mediumCell : null, isMobile && { width: 32, height: 45 }],
    [isLarge],
  );

  const config = GAME_TYPES_CONFIG[props.gameType];

  let operands: OPERAND_TYPE[] = [];

  if (position === SLIDER_POSITION.LEFT && hasLeftOperands(config)) {
    operands = config.leftOperands;
  } else if (position === SLIDER_POSITION.RIGHT && hasRightOperands(config)) {
    operands = config.rightOperands;
  } else if (hasOperands(config)) {
    operands = config.operands;
  }

  return (
    <View
      style={
        !isLarge && !isMobile
          ? [
              styles.mediumContainer,
              { width: rowMediumWidth },
              operandRowButtonCount === 3 && {
                // width: 250,
                width: 90,
                justifyContent: position === SLIDER_POSITION.LEFT ? 'flex-end' : 'flex-start',
              },
            ]
          : [
              styles.container,
              { width: rowFullWidth },
              operandRowButtonCount === 3 && {
                width: isMobile || breakPointControls ? 120 : 250,
                justifyContent: position === SLIDER_POSITION.LEFT ? 'flex-end' : 'flex-start',
              },
            ]
      }
    >
      {operands.map((el, index: number) => (
        <Pressable
          style={[
            {
              borderBottomColor: '#FFEABA',
              borderBottomWidth: 3,
              marginHorizontal: !isLarge ? 1 : 3,
              marginVertical: !isLarge ? 3 : 6,
              borderRadius: 5,
            },
            isEqual(el, props.value) ? styles.activeCell : null,
          ]}
          key={index}
          onPress={onPress(el)}
        >
          <LinearGradient
            style={[gradientStyle(), isEqual(el, props.value) && { borderColor: '#F7E4D0' }]}
            colors={isEqual(el, props.value) ? props.colors : whiteLinearGradient}
          >
            <Text
              maxFontSizeMultiplier={1}
              style={[styles.text, isEqual(el, props.value) ? styles.activeText : null]}
            >
              {renderValue(el, props.gameType)}
            </Text>
          </LinearGradient>
        </Pressable>
      ))}
      <View
        style={[
          styles.fakeCell,
          {
            marginHorizontal: !isLarge ? 1 : 3,
            marginVertical: !isLarge ? 3 : 6,
          },
          !isLarge && !isMobile ? styles.mediumCell : null,
        ]}
      />
      <View
        style={[
          styles.fakeCell,
          {
            marginHorizontal: !isLarge ? 1 : 3,
            marginVertical: !isLarge ? 3 : 6,
          },
          !isLarge && !isMobile ? styles.mediumCell : null,
        ]}
      />
      {!(operandRowButtonCount && operandRowButtonCount < 4) && (
        <>
          <View
            style={[
              styles.fakeCell,
              {
                marginHorizontal: !isLarge ? 1 : 3,
                marginVertical: !isLarge ? 3 : 6,
              },
              !isLarge && !isMobile ? styles.mediumCell : null,
            ]}
          />
        </>
      )}
    </View>
  );
});

LargeSlider.displayName = 'LargeSlider';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  mediumContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  cell: {
    width: 54,
    height: 61,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    borderBottomWidth: 2,
    borderLeftWidth: 2,
    borderColor: '#FFEABA',
    marginBottom: -2,
    paddingTop: 4,
  },
  mediumCell: {
    width: 26,
    height: 30,
    // width: 40,
    // height: 51,
    paddingTop: 2,
  },
  activeCell: {
    borderBottomWidth: 0,
  },
  text: {
    fontFamily: 'Secular One',
    fontSize: 23,
    color: '#000000',
  },
  activeText: {
    color: '#000000',
  },
  fakeCell: {
    width: 32,
    height: 0,
  },
  fakeMediumCell: {
    width: 26,
    height: 0,
  },
});
